import { handoffRedirectTarget } from '../auth/oauthHandoff';
import config from '../config';
import type {
    AccountList,
    AccountView,
    Claimed,
    CodeCheck,
    CodeOption,
    CreateBody,
    Created,
    Credentials,
    EmailCodeSent,
    EmailVerified,
    LogoState,
    OAuthStarted,
    Released,
    RestechList,
    SignupPreview,
} from './types';

// The self-signup app's calls to the pre-tenant API (contract: apiv2/signup.openapi.yaml, mounted at
// /v2/signup/; backend plan 2026-10-17-dashboard-self-signup-invite-links §6-§8).
//
// Plain fetch: there is no dashboard session here, and the data provider would treat a 401/403 as a
// signed-out dashboard user. `preview` and `claim` are public; every other call carries
// `Authorization: Signup <session>`. signupContract.test.js holds ENDPOINTS to the contract's paths
// and methods (the spec has no operationIds, so the keys are ours).

export const SIGNUP_API_BASE = `${config.apiUrl.replace(/\/v\d+.*$/, '')}/v2/signup`;

export const ENDPOINTS = {
    preview: { method: 'POST', path: '/preview/' },
    claim: { method: 'POST', path: '/claim/' },
    release: { method: 'POST', path: '/release/' },
    restechs: { method: 'GET', path: '/restechs/' },
    accounts: { method: 'POST', path: '/{restech}/accounts/' },
    account: { method: 'POST', path: '/{restech}/account/' },
    logo: { method: 'GET', path: '/logo/' },
    logoImage: { method: 'GET', path: '/logo/{name}/' },
    codeCheck: { method: 'POST', path: '/code_check/' },
    emailSendCode: { method: 'POST', path: '/email/send_code/' },
    emailVerify: { method: 'POST', path: '/email/verify/' },
    create: { method: 'POST', path: '/{restech}/create/' },
    oauthStart: { method: 'POST', path: '/{restech}/oauth/start/' },
} as const;

export type OperationId = keyof typeof ENDPOINTS;

/** `oauth/start`'s `authorize_url` when it is what the contract says it is — our own begin hop on the API origin this
 *  client calls — else null: an absolute https URL (http only where `allowHttp`, local development) whose origin is
 *  `base`'s. Nothing else is navigated to, whatever the answer says. */
export function ownBeginHop(raw: unknown, { allowHttp = false, base = SIGNUP_API_BASE }: { allowHttp?: boolean; base?: string } = {}): string | null {
    const target = handoffRedirectTarget(raw, { allowHttp });
    if (!target) {
        return null;
    }
    try {
        return new URL(target).origin === new URL(base).origin ? target : null;
    } catch (_e) {
        return null;
    }
}

/** The calls made without a session (`security: []` in the contract). */
export const PUBLIC_OPERATIONS: OperationId[] = ['preview', 'claim'];

/** The session refusals of every `Signup` endpoint (401). */
export const SESSION_ERROR_CODES = ['session_missing', 'session_invalid', 'session_expired', 'session_revoked'];

/** Codes the page gives a failure that has no contract `code`: no answer, a 404 without a code (a
 *  proxy or an older backend; the contract's kill switch answers `signup_disabled`), a server error
 *  without a JSON body. */
export const CLIENT_ERROR_CODES = ['network', 'disabled', 'server_error'] as const;

export class SignupApiError extends Error {
    status: number;
    code: string;
    detail: string | null;
    errors: Record<string, any>;
    body: any;
    retryAfter: number | null;

    constructor(status: number, body: any, retryAfter: number | null = null) {
        const detail = body && typeof body.detail === 'string' && body.detail ? body.detail : null;
        super(detail || `Signup request failed (${status})`);
        this.name = 'SignupApiError';
        this.status = status;
        this.body = body;
        this.detail = detail;
        this.retryAfter = retryAfter;
        this.errors = body && body.errors && typeof body.errors === 'object' ? body.errors : {};
        if (body && typeof body.code === 'string' && body.code) {
            this.code = body.code;
        } else if (status === 0) {
            this.code = 'network';
        } else if (status === 404) {
            this.code = 'disabled';
        } else {
            this.code = 'server_error';
        }
    }

    get isSessionError() {
        return this.status === 401 || SESSION_ERROR_CODES.includes(this.code);
    }

    get claimedSince(): string | null {
        return (this.body && this.body.claimed_since) || null;
    }

    get signInUrl(): string | null {
        return (this.body && this.body.sign_in_url) || null;
    }

    get attemptsLeft(): number | null {
        return this.body && typeof this.body.attempts_left === 'number' ? this.body.attempts_left : null;
    }

    /** `409 code_taken` only: the fresh tenant code options to offer again (older backends: none). */
    get codeOptions(): CodeOption[] | null {
        const options = this.body && this.body.code_options;
        return Array.isArray(options) && options.length ? options : null;
    }
}

export function buildUrl(operation: OperationId, pathParams: Record<string, string> = {}, base = SIGNUP_API_BASE) {
    let path: string = ENDPOINTS[operation].path;
    Object.entries(pathParams).forEach(([name, value]) => {
        path = path.replace(`{${name}}`, encodeURIComponent(value));
    });
    return `${base}${path}`;
}

function retryAfterOf(res: Response): number | null {
    const raw = res.headers && typeof res.headers.get === 'function' ? res.headers.get('Retry-After') : null;
    const seconds = raw == null ? NaN : parseInt(raw, 10);
    return Number.isFinite(seconds) && seconds >= 0 ? seconds : null;
}

interface ClientOptions {
    /** The session of this page's claim, or null before the claim. */
    getSession: () => string | null;
    /** A fresh session after `session_expired` (claim again with the stored claim secret), or null
     *  when there is none. Called once per expired call; concurrent calls share one renewal. */
    renewSession?: () => Promise<string | null>;
    base?: string;
}

interface CallOptions {
    path?: Record<string, string>;
    body?: unknown;
    blob?: boolean;
}

export function createSignupApi({ getSession, renewSession, base = SIGNUP_API_BASE }: ClientOptions) {
    let renewal: Promise<string | null> | null = null;
    const renewOnce = () => {
        if (!renewSession) {
            return Promise.resolve(null);
        }
        if (!renewal) {
            renewal = renewSession()
                .catch(() => null)
                .finally(() => {
                    renewal = null;
                });
        }
        return renewal;
    };

    const send = async (operation: OperationId, { path = {}, body }: CallOptions, session: string | null) => {
        const { method } = ENDPOINTS[operation];
        const headers: Record<string, string> = { Accept: 'application/json' };
        if (body !== undefined) {
            headers['Content-Type'] = 'application/json';
        }
        if (!PUBLIC_OPERATIONS.includes(operation) && session) {
            headers.Authorization = `Signup ${session}`;
        }
        try {
            return await fetch(buildUrl(operation, path, base), {
                method,
                headers,
                cache: 'no-store',
                credentials: 'omit',
                body: body !== undefined ? JSON.stringify(body) : undefined,
            });
        } catch (_e) {
            throw new SignupApiError(0, null);
        }
    };

    const failure = async (res: Response) => {
        let data: any = null;
        try {
            data = await res.json();
        } catch (_e) {
            data = null;
        }
        return new SignupApiError(res.status, data, retryAfterOf(res));
    };

    const call = async <T>(operation: OperationId, options: CallOptions = {}): Promise<T> => {
        let res = await send(operation, options, getSession());
        if (res.status === 401 && !PUBLIC_OPERATIONS.includes(operation)) {
            const error = await failure(res);
            if (error.code !== 'session_expired') {
                throw error;
            }
            const fresh = await renewOnce();
            if (!fresh) {
                throw error;
            }
            res = await send(operation, options, fresh);
        }
        if (!res.ok) {
            throw await failure(res);
        }
        if (options.blob) {
            return (await res.blob()) as unknown as T;
        }
        try {
            return (await res.json()) as T;
        } catch (_e) {
            throw new SignupApiError(res.status, null);
        }
    };

    // The secrets are never sent as `code` (a tenant code's name, kept visible in the backend's Sentry): the invite
    // code is `invite_code`, the e-mail code `verification_code`, both on the backend's denylist.
    const withSecret = (code: string, claimSecret?: string | null) =>
        (claimSecret ? { invite_code: code, claim_secret: claimSecret } : { invite_code: code });

    return {
        preview: (code: string, claimSecret?: string | null) => call<SignupPreview>('preview', { body: withSecret(code, claimSecret) }),
        claim: (code: string, claimSecret?: string | null) => call<Claimed>('claim', { body: withSecret(code, claimSecret) }),
        release: () => call<Released>('release'),
        restechs: () => call<RestechList>('restechs'),
        accounts: (restech: string, credentials: Credentials) => call<AccountList>('accounts', { path: { restech }, body: { credentials } }),
        account: (restech: string, credentials: Credentials, account: string) =>
            call<AccountView>('account', { path: { restech }, body: { credentials, account } }),
        logo: () => call<LogoState>('logo'),
        logoImage: (name: string) => call<Blob>('logoImage', { path: { name }, blob: true }),
        codeCheck: (code: string) => call<CodeCheck>('codeCheck', { body: { code } }),
        sendEmailCode: (email: string) => call<EmailCodeSent>('emailSendCode', { body: { email } }),
        verifyEmail: (email: string, code: string) =>
            call<EmailVerified>('emailVerify', { body: { email, verification_code: code } }),
        create: (restech: string, body: CreateBody) => call<Created>('create', { path: { restech }, body }),
        // "Connect with <booking system>": the body is the oauth mode's fields (e.g. `{domain}`), never a credential.
        oauthStart: (restech: string, fields: Record<string, string>) =>
            call<OAuthStarted>('oauthStart', { path: { restech }, body: fields }),
    };
}

export type SignupApi = ReturnType<typeof createSignupApi>;
